import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Spinner } from "flowbite-react";
import { Order } from "../../../models";
import { firestore } from "../../../firebaseConfig";
import CreateOrder from "../create-order/CreateOrder";
import CreateOrderStepsEnum from "../create-order/CreateOrderStepsEnum";
import { useTranslation } from "react-i18next";

const EditOrder: React.FC = () => {
  const { t } = useTranslation(["common"]);
  const { id } = useParams<{ id: string }>();
  const [order, setOrder] = useState<Order | null>(null);
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchOrder = async () => {
      if (!id) {
        // No order id provided – stop loading and show not-found state
        setLoading(false);
        return;
      }
      try {
        const doc = await firestore
          .collection(Order.collectionName)
          .doc(id)
          .withConverter(Order.converter)
          .get();
        if (doc.exists) {
          const loaded = doc.data() as Order;
          // Ensure each route item has isPickup explicitly set when missing
          if (loaded.route && loaded.route.length > 0) {
            loaded.route = loaded.route.map((ri) => ({
              ...ri,
              isPickup:
                ri.isPickup !== undefined ? ri.isPickup : ri.stopNumber === 0,
            }));
          }
          setOrder(loaded);
        }
      } catch (error) {
        console.error("Failed to load order", error);
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full py-10">
        <Spinner />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="flex items-center justify-center h-full py-10">
        <p className="text-muted">
          {t("common:order_not_found", "Order not found")}
        </p>
      </div>
    );
  }

  const fullFlow = searchParams.get("full") === "1";

  return (
    <CreateOrder
      initialOrder={order}
      isEdit
      {...(!fullFlow && {
        editableSteps: [
          CreateOrderStepsEnum.ROUTE_SELECTION,
          CreateOrderStepsEnum.PRODUCT_MANAGEMENT,
        ],
      })}
      onCompleted={() => navigate(-1)}
    />
  );
};

export default EditOrder; 
